import React from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { useGetClaimCampaigns } from '~/hooks/apis/loyalty/useGetClaimCampaigns';

import type { ClaimCampaign } from '~/types/loyaltyContract';

import { type CampaignAction, downloadClaimsTemplate, formatAction } from '../csvHelpers';

type ClaimMethod = 'bulk' | 'single';

interface Props {
  selectedMethod: ClaimMethod | null;
  selectedCampaign: ClaimCampaign | null;
  onSelectMethod: (method: ClaimMethod) => void;
  onSelectCampaign: (campaign: ClaimCampaign | null) => void;
  onNext: () => void;
}

export default function SelectMethodStep({
  selectedMethod,
  selectedCampaign,
  onSelectMethod,
  onSelectCampaign,
  onNext,
}: Props) {
  const { data: campaigns = [], isLoading, error } = useGetClaimCampaigns();

  const handleCampaignChange = (campaignId: string) => {
    const campaign = campaigns.find((c) => c.id === campaignId) ?? null;
    onSelectCampaign(campaign);
  };

  const canProceed = !!selectedMethod && !!selectedCampaign;

  return (
    <Box>
      {/* Campaign Selection */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 3, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select Campaign
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Choose the campaign to process redemptions for.
        </Typography>

        {isLoading && <CircularProgress size={24} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load campaigns: {error.message}
          </Alert>
        )}

        {!isLoading && !error && (
          <FormControl fullWidth size="small">
            <InputLabel>Campaign</InputLabel>
            <Select
              value={selectedCampaign?.id ?? ''}
              label="Campaign"
              onChange={(e) => handleCampaignChange(e.target.value)}
            >
              {campaigns.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedCampaign && (
          <Box mt={1.5}>
            <Typography variant="caption" color="text.secondary" display="block">
              Validator: {selectedCampaign.validatorType} &middot; Max redemptions per user:{' '}
              {selectedCampaign.maxClaimsPerUser}
            </Typography>
            {selectedCampaign.actions?.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  bgcolor: (theme) => theme.palette.info.main + '0D',
                  border: 1,
                  borderColor: (theme) => theme.palette.info.main + '33',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" fontWeight={600} display="block" mb={0.75}>
                  Customers will receive:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedCampaign.actions.map((action, i) => (
                    <Chip
                      key={i}
                      label={formatAction(action as CampaignAction)}
                      size="small"
                      color="info"
                      variant="filled"
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Method Selection */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Choose your upload method
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Select how you would like to process campaign redemptions.
        </Typography>

        <Grid container spacing={3} columns={12}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                borderColor: selectedMethod === 'bulk' ? 'primary.main' : 'divider',
                borderWidth: selectedMethod === 'bulk' ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => onSelectMethod('bulk')} sx={{ p: 3 }}>
                <CardContent>
                  <UploadFileIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Bulk Upload
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload a CSV file to submit redemptions for multiple customers simultaneously.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                borderColor: selectedMethod === 'single' ? 'primary.main' : 'divider',
                borderWidth: selectedMethod === 'single' ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => onSelectMethod('single')} sx={{ p: 3 }}>
                <CardContent>
                  <PersonAddIcon color="warning" sx={{ fontSize: 40, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Single Manual Entry
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Submit a redemption on behalf of an individual customer.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
        <Typography variant="body2" color="text.secondary">
          You'll be able to review all changes before they go live.
        </Typography>
        <Button variant="outlined" onClick={onNext} disabled={!canProceed}>
          Next Step
        </Button>
      </Stack>

      <Grid container spacing={3} mt={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Need a template?
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Download our CSV template for bulk uploads to ensure data formatting is correct.
            </Typography>
            <Button size="small" startIcon={<DownloadIcon />} onClick={downloadClaimsTemplate}>
              Download CSV Template
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
